import React, { useState, useEffect, useCallback } from 'react';

const CulturePodAdminPanel = ({ API, adminToken, isDarkMode, showNotification }) => {
  const [activeSubTab, setActiveSubTab] = useState('episodes');
  const [categories, setCategories] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [settings, setSettings] = useState({ pageVisible: true, isProOnly: false, headerTitle: 'Culture Pod', headerSubtitle: 'Discover Morocco through stories' });
  const [loading, setLoading] = useState(true);
  const [editingEpisode, setEditingEpisode] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showEpisodeForm, setShowEpisodeForm] = useState(false);
  const [showCategoryForm, setShowCategoryForm] = useState(false);

  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, epRes, setRes] = await Promise.all([
        fetch(`${API}/admin/culture-pod/categories`, { headers }),
        fetch(`${API}/admin/culture-pod/episodes`, { headers }),
        fetch(`${API}/admin/culture-pod/settings`, { headers })
      ]);
      if (catRes.ok) { const d = await catRes.json(); setCategories(d.categories || []); }
      if (epRes.ok) { const d = await epRes.json(); setEpisodes(d.episodes || []); }
      if (setRes.ok) { const d = await setRes.json(); setSettings(d); }
    } catch (e) { console.error('Load error:', e); }
    setLoading(false);
  }, [API, adminToken]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Category CRUD ──
  const saveCategory = async (cat) => {
    const isNew = !cat.id || cat._isNew;
    const url = isNew ? `${API}/admin/culture-pod/categories` : `${API}/admin/culture-pod/categories/${cat.id}`;
    const method = isNew ? 'POST' : 'PUT';
    const body = { ...cat }; delete body._isNew;
    const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
    if (res.ok) { showNotification('Category saved', 'success'); loadData(); setShowCategoryForm(false); setEditingCategory(null); }
    else showNotification('Failed to save category', 'error');
  };

  const deleteCategory = async (id) => {
    const res = await fetch(`${API}/admin/culture-pod/categories/${id}`, { method: 'DELETE', headers });
    if (res.ok) { showNotification('Category deleted', 'success'); loadData(); }
  };

  // ── Episode CRUD ──
  const saveEpisode = async (ep) => {
    const isNew = !ep.id || ep._isNew;
    const url = isNew ? `${API}/admin/culture-pod/episodes` : `${API}/admin/culture-pod/episodes/${ep.id}`;
    const method = isNew ? 'POST' : 'PUT';
    const body = { ...ep }; delete body._isNew;
    const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
    if (res.ok) { showNotification('Episode saved', 'success'); loadData(); setShowEpisodeForm(false); setEditingEpisode(null); }
    else showNotification('Failed to save episode', 'error');
  };

  const deleteEpisode = async (id) => {
    const res = await fetch(`${API}/admin/culture-pod/episodes/${id}`, { method: 'DELETE', headers });
    if (res.ok) { showNotification('Episode deleted', 'success'); loadData(); }
  };

  // ── Settings ──
  const saveSettings = async () => {
    const res = await fetch(`${API}/admin/culture-pod/settings`, { method: 'PUT', headers, body: JSON.stringify(settings) });
    if (res.ok) showNotification('Settings saved', 'success');
    else showNotification('Failed to save settings', 'error');
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>Loading Culture Pod...</div>;

  const cardStyle = { background: isDarkMode ? '#1e293b' : '#fff', borderRadius: 12, padding: 20, marginBottom: 16, border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}` };
  const inputStyle = { width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: isDarkMode ? '#0f172a' : '#f8fafc', color: isDarkMode ? '#e2e8f0' : '#1e293b', fontSize: 14, marginBottom: 8 };
  const btnStyle = { padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600 };
  const primaryBtn = { ...btnStyle, background: '#8b5cf6', color: '#fff' };
  const dangerBtn = { ...btnStyle, background: '#ef4444', color: '#fff' };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 16, color: isDarkMode ? '#e2e8f0' : '#1e293b' }}>Culture Pod Admin</h2>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['episodes', 'categories', 'settings'].map(tab => (
          <button key={tab} onClick={() => setActiveSubTab(tab)} style={{ ...btnStyle, background: activeSubTab === tab ? '#8b5cf6' : (isDarkMode ? '#334155' : '#e2e8f0'), color: activeSubTab === tab ? '#fff' : (isDarkMode ? '#cbd5e1' : '#475569'), textTransform: 'capitalize' }}>
            {tab}
          </button>
        ))}
      </div>

      {/* ── CATEGORIES TAB ── */}
      {activeSubTab === 'categories' && (
        <div>
          <button onClick={() => { setEditingCategory({ name: '', icon: '🎧', _isNew: true }); setShowCategoryForm(true); }} style={primaryBtn}>+ Add Category</button>
          <div style={{ marginTop: 16 }}>
            {categories.map(cat => (
              <div key={cat.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 16, color: isDarkMode ? '#e2e8f0' : '#1e293b' }}>{cat.icon} {cat.name}</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => { setEditingCategory(cat); setShowCategoryForm(true); }} style={{ ...btnStyle, background: '#3b82f6', color: '#fff' }}>Edit</button>
                  <button onClick={() => deleteCategory(cat.id)} style={dangerBtn}>Delete</button>
                </div>
              </div>
            ))}
          </div>
          {showCategoryForm && editingCategory && (
            <div style={{ ...cardStyle, marginTop: 16, border: '2px solid #8b5cf6' }}>
              <h3 style={{ marginBottom: 12, color: isDarkMode ? '#e2e8f0' : '#1e293b' }}>{editingCategory._isNew ? 'New Category' : 'Edit Category'}</h3>
              <input style={inputStyle} placeholder="Name" value={editingCategory.name || ''} onChange={e => setEditingCategory({ ...editingCategory, name: e.target.value })} />
              <input style={inputStyle} placeholder="Icon (emoji)" value={editingCategory.icon || ''} onChange={e => setEditingCategory({ ...editingCategory, icon: e.target.value })} />
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button onClick={() => saveCategory(editingCategory)} style={primaryBtn}>Save</button>
                <button onClick={() => { setShowCategoryForm(false); setEditingCategory(null); }} style={btnStyle}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── EPISODES TAB ── */}
      {activeSubTab === 'episodes' && (
        <div>
          <button onClick={() => { setEditingEpisode({ title: '', description: '', categoryId: '', coverImageUrl: '', audioUrl: '', duration: '', isFree: true, chapters: [], transcript: [], phrases: [], quizQuestions: [], _isNew: true }); setShowEpisodeForm(true); }} style={primaryBtn}>+ Add Episode</button>
          <div style={{ marginTop: 16 }}>
            {episodes.map(ep => {
              const cat = categories.find(c => c.id === ep.categoryId);
              return (
                <div key={ep.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 16 }}>
                  {ep.coverImageUrl && <img src={ep.coverImageUrl} alt="" style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover' }} />}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: isDarkMode ? '#e2e8f0' : '#1e293b' }}>{ep.title}</div>
                    <div style={{ fontSize: 13, color: '#94a3b8' }}>{cat?.icon} {cat?.name || 'No category'} | {ep.duration} | {ep.isFree ? 'FREE' : 'PRO'}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => { setEditingEpisode(ep); setShowEpisodeForm(true); }} style={{ ...btnStyle, background: '#3b82f6', color: '#fff' }}>Edit</button>
                    <button onClick={() => deleteEpisode(ep.id)} style={dangerBtn}>Delete</button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Episode Form */}
          {showEpisodeForm && editingEpisode && (
            <EpisodeForm
              episode={editingEpisode}
              categories={categories}
              isDarkMode={isDarkMode}
              API={API}
              adminToken={adminToken}
              onSave={saveEpisode}
              onCancel={() => { setShowEpisodeForm(false); setEditingEpisode(null); }}
            />
          )}
        </div>
      )}

      {/* ── SETTINGS TAB ── */}
      {activeSubTab === 'settings' && (
        <div style={cardStyle}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: isDarkMode ? '#e2e8f0' : '#1e293b', marginBottom: 12 }}>
              <input type="checkbox" checked={settings.pageVisible} onChange={e => setSettings({ ...settings, pageVisible: e.target.checked })} /> Page Visible
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: isDarkMode ? '#e2e8f0' : '#1e293b', marginBottom: 12 }}>
              <input type="checkbox" checked={settings.isProOnly} onChange={e => setSettings({ ...settings, isProOnly: e.target.checked })} /> Pro Only
            </label>
          </div>
          <input style={inputStyle} placeholder="Header Title" value={settings.headerTitle || ''} onChange={e => setSettings({ ...settings, headerTitle: e.target.value })} />
          <input style={inputStyle} placeholder="Header Subtitle" value={settings.headerSubtitle || ''} onChange={e => setSettings({ ...settings, headerSubtitle: e.target.value })} />
          <button onClick={saveSettings} style={{ ...primaryBtn, marginTop: 8 }}>Save Settings</button>
        </div>
      )}
    </div>
  );
};

// ── Episode Form (sub-component for admin) ──
const EpisodeForm = ({ episode, categories, isDarkMode, API, adminToken, onSave, onCancel }) => {
  const [ep, setEp] = useState({ ...episode });

  const inputStyle = { width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: isDarkMode ? '#0f172a' : '#f8fafc', color: isDarkMode ? '#e2e8f0' : '#1e293b', fontSize: 14, marginBottom: 8 };
  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: isDarkMode ? '#94a3b8' : '#64748b', marginBottom: 4, marginTop: 12 };
  const sectionStyle = { background: isDarkMode ? '#0f172a' : '#f1f5f9', borderRadius: 8, padding: 16, marginTop: 12 };
  const btnStyle = { padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 };

  // Chapters
  const addChapter = () => setEp({ ...ep, chapters: [...(ep.chapters || []), { name: '', timestamp: 0 }] });
  const updateChapter = (i, field, val) => { const c = [...(ep.chapters || [])]; c[i] = { ...c[i], [field]: field === 'timestamp' ? Number(val) : val }; setEp({ ...ep, chapters: c }); };
  const removeChapter = (i) => { const c = [...(ep.chapters || [])]; c.splice(i, 1); setEp({ ...ep, chapters: c }); };

  // Transcript
  const addTranscript = () => setEp({ ...ep, transcript: [...(ep.transcript || []), { text: '', startTime: 0, endTime: 0, images: [] }] });
  const updateTranscript = (i, field, val) => { const t = [...(ep.transcript || [])]; t[i] = { ...t[i], [field]: field === 'text' ? val : Number(val) }; setEp({ ...ep, transcript: t }); };
  const removeTranscript = (i) => { const t = [...(ep.transcript || [])]; t.splice(i, 1); setEp({ ...ep, transcript: t }); };
  const addTranscriptImage = (ti) => { const t = [...(ep.transcript || [])]; const entry = t[ti]; const imgs = [...(entry.images || [])]; imgs.push({ url: '', startTime: entry.startTime, endTime: entry.endTime }); t[ti] = { ...entry, images: imgs }; setEp({ ...ep, transcript: t }); };
  const updateTranscriptImage = (ti, ii, field, val) => { const t = [...(ep.transcript || [])]; const imgs = [...(t[ti].images || [])]; imgs[ii] = { ...imgs[ii], [field]: field === 'url' ? val : Number(val) }; t[ti] = { ...t[ti], images: imgs }; setEp({ ...ep, transcript: t }); };
  const removeTranscriptImage = (ti, ii) => { const t = [...(ep.transcript || [])]; const imgs = [...(t[ti].images || [])]; imgs.splice(ii, 1); t[ti] = { ...t[ti], images: imgs }; setEp({ ...ep, transcript: t }); };

  // Phrases
  const addPhrase = () => setEp({ ...ep, phrases: [...(ep.phrases || []), { latin: '', arabic: '', english: '' }] });
  const updatePhrase = (i, field, val) => { const p = [...(ep.phrases || [])]; p[i] = { ...p[i], [field]: val }; setEp({ ...ep, phrases: p }); };
  const removePhrase = (i) => { const p = [...(ep.phrases || [])]; p.splice(i, 1); setEp({ ...ep, phrases: p }); };

  // Quiz
  const addQuiz = () => setEp({ ...ep, quizQuestions: [...(ep.quizQuestions || []), { question: '', options: ['', '', '', ''], correctIndex: 0 }] });
  const updateQuiz = (i, field, val) => { const q = [...(ep.quizQuestions || [])]; q[i] = { ...q[i], [field]: val }; setEp({ ...ep, quizQuestions: q }); };
  const updateQuizOption = (qi, oi, val) => { const q = [...(ep.quizQuestions || [])]; q[qi] = { ...q[qi], options: q[qi].options.map((o, j) => j === oi ? val : o) }; setEp({ ...ep, quizQuestions: q }); };
  const removeQuiz = (i) => { const q = [...(ep.quizQuestions || [])]; q.splice(i, 1); setEp({ ...ep, quizQuestions: q }); };

  // Image upload helper
  const uploadImage = async (callback) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/webp,image/gif';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) { alert('Max 5MB'); return; }
      const form = new FormData();
      form.append('file', file);
      try {
        const res = await fetch(`${API}/admin/culture-pod/upload-image`, { method: 'POST', headers: { 'Authorization': `Bearer ${adminToken}` }, body: form });
        if (res.ok) { const data = await res.json(); callback(API.replace('/api', '') + data.url); }
        else { alert('Upload failed'); }
      } catch { alert('Upload failed'); }
    };
    input.click();
  };

  // Script Parser — auto-generate chapters + transcript from pasted script
  const [scriptText, setScriptText] = useState('');
  const parseScript = () => {
    if (!scriptText.trim()) return;
    const WPM = 150;
    const chapterRegex = /^---\s*(.+?)\s*---$/gm;
    const parts = [];
    let lastIdx = 0;
    let match;
    while ((match = chapterRegex.exec(scriptText)) !== null) {
      if (lastIdx > 0 || match.index > 0) {
        const text = scriptText.slice(lastIdx, match.index).trim();
        if (parts.length > 0 && text) parts[parts.length - 1].text = text;
      }
      parts.push({ name: match[1], text: '' });
      lastIdx = match.index + match[0].length;
    }
    if (parts.length > 0) {
      const remaining = scriptText.slice(lastIdx).trim();
      if (remaining) parts[parts.length - 1].text = remaining;
    }
    if (parts.length === 0) {
      parts.push({ name: 'Full Episode', text: scriptText.trim() });
    }
    let currentTime = 0;
    const chapters = [];
    const transcript = [];
    for (const part of parts) {
      chapters.push({ name: part.name, timestamp: Math.round(currentTime) });
      const paragraphs = part.text.split(/\n\s*\n/).map(p => p.replace(/\n/g, ' ').trim()).filter(Boolean);
      for (const para of paragraphs) {
        const wordCount = para.split(/\s+/).length;
        const duration = (wordCount / WPM) * 60;
        const startTime = Math.round(currentTime);
        currentTime += duration;
        transcript.push({ text: para, startTime, endTime: Math.round(currentTime), images: [] });
      }
    }
    setEp(prev => ({ ...prev, chapters, transcript }));
    setScriptText('');
  };

  return (
    <div style={{ background: isDarkMode ? '#1e293b' : '#fff', borderRadius: 12, padding: 24, marginTop: 16, border: '2px solid #8b5cf6', maxHeight: '80vh', overflowY: 'auto' }}>
      <h3 style={{ marginBottom: 16, color: isDarkMode ? '#e2e8f0' : '#1e293b', fontSize: 18 }}>{ep._isNew ? 'New Episode' : 'Edit Episode'}</h3>

      {/* Basic Info */}
      <label style={labelStyle}>Title</label>
      <input style={inputStyle} value={ep.title || ''} onChange={e => setEp({ ...ep, title: e.target.value })} />

      <label style={labelStyle}>Description</label>
      <textarea style={{ ...inputStyle, minHeight: 60 }} value={ep.description || ''} onChange={e => setEp({ ...ep, description: e.target.value })} />

      <label style={labelStyle}>Category</label>
      <select style={inputStyle} value={ep.categoryId || ''} onChange={e => setEp({ ...ep, categoryId: e.target.value })}>
        <option value="">Select category</option>
        {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
      </select>

      <label style={labelStyle}>Cover Subtitle</label>
      <input style={inputStyle} value={ep.coverSubtitle || ''} onChange={e => setEp({ ...ep, coverSubtitle: e.target.value })} placeholder="e.g., Moroccan Life, Culture Series..." />

      <label style={labelStyle}>Cover Style</label>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        {[
          'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
          'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
          'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
          'linear-gradient(135deg, #fccb90 0%, #d57eeb 100%)',
          'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
        ].map((g, i) => (
          <div key={i} onClick={() => setEp({ ...ep, coverStyle: `gradient-${i}`, coverImageUrl: '' })}
            style={{ width: 48, height: 48, borderRadius: 10, background: g, cursor: 'pointer', border: ep.coverStyle === `gradient-${i}` ? '3px solid #fff' : '3px solid transparent', boxShadow: ep.coverStyle === `gradient-${i}` ? '0 0 0 2px #8b5cf6' : '0 1px 4px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, opacity: ep.coverStyle === `gradient-${i}` ? 1 : 0.7 }}>
            🎙️
          </div>
        ))}
        <div onClick={() => setEp({ ...ep, coverStyle: '' })}
          style={{ width: 48, height: 48, borderRadius: 10, background: isDarkMode ? '#334155' : '#e2e8f0', cursor: 'pointer', border: !ep.coverStyle ? '3px solid #8b5cf6' : '3px solid transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, color: isDarkMode ? '#94a3b8' : '#64748b' }}>
          URL
        </div>
      </div>
      {!ep.coverStyle && (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <button onClick={() => uploadImage(url => setEp(prev => ({ ...prev, coverImageUrl: url })))} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>Upload Image</button>
            <input style={{ ...inputStyle, flex: 1, marginBottom: 0 }} value={ep.coverImageUrl || ''} onChange={e => setEp({ ...ep, coverImageUrl: e.target.value })} placeholder="Or paste image URL..." />
          </div>
          {ep.coverImageUrl && <img src={ep.coverImageUrl} alt="" style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 8, marginBottom: 8 }} />}
        </>
      )}
      {ep.coverStyle && (
        <div style={{ width: 200, height: 120, borderRadius: 12, overflow: 'hidden', marginBottom: 8, position: 'relative', background: ['linear-gradient(135deg, #667eea 0%, #764ba2 100%)','linear-gradient(135deg, #f093fb 0%, #f5576c 100%)','linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)','linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)','linear-gradient(135deg, #fa709a 0%, #fee140 100%)','linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)','linear-gradient(135deg, #fccb90 0%, #d57eeb 100%)','linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)'][parseInt(ep.coverStyle.split('-')[1]) || 0], display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: 32, opacity: 0.3 }}>🎙️</div>
          {ep.coverSubtitle && <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, zIndex: 1 }}>{ep.coverSubtitle}</div>}
          <div style={{ fontSize: 11, color: '#fff', fontWeight: 700, textAlign: 'center', zIndex: 1, textShadow: '0 1px 3px rgba(0,0,0,0.3)', padding: '0 8px' }}>{ep.title || 'Episode Title'}</div>
        </div>
      )}

      <label style={labelStyle}>Audio URL (Dropbox)</label>
      <input style={inputStyle} value={ep.audioUrl || ''} onChange={e => setEp({ ...ep, audioUrl: e.target.value })} placeholder="https://dl.dropboxusercontent.com/..." />

      <label style={labelStyle}>Duration</label>
      <input style={inputStyle} value={ep.duration || ''} onChange={e => setEp({ ...ep, duration: e.target.value })} placeholder="18:30" />

      <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" checked={ep.isFree !== false} onChange={e => setEp({ ...ep, isFree: e.target.checked })} /> Free Episode
      </label>

      {/* Script Parser */}
      <div style={sectionStyle}>
        <h4 style={{ color: isDarkMode ? '#e2e8f0' : '#1e293b', margin: '0 0 8px 0' }}>Paste Full Script</h4>
        <p style={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: 12, margin: '0 0 8px 0' }}>
          Use --- Chapter Name --- markers to separate sections. Timestamps auto-estimated from word count.
        </p>
        <textarea
          style={{ ...inputStyle, minHeight: 150, fontFamily: 'monospace', fontSize: 13 }}
          placeholder={"--- Introduction ---\nWelcome to Culture Pod...\n\n--- Cultural Deep Dive ---\nFirst, let's talk about..."}
          value={scriptText}
          onChange={e => setScriptText(e.target.value)}
        />
        <button onClick={parseScript} style={{ ...btnStyle, background: '#8b5cf6', color: '#fff', marginTop: 8 }}>
          Parse Script &rarr; Auto-generate Chapters & Transcript
        </button>
      </div>

      {/* Chapters */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h4 style={{ color: isDarkMode ? '#e2e8f0' : '#1e293b', margin: 0 }}>Chapters</h4>
          <button onClick={addChapter} style={{ ...btnStyle, background: '#8b5cf6', color: '#fff' }}>+ Add</button>
        </div>
        {(ep.chapters || []).map((ch, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
            <input style={{ ...inputStyle, flex: 1, marginBottom: 0 }} placeholder="Chapter name" value={ch.name} onChange={e => updateChapter(i, 'name', e.target.value)} />
            <input style={{ ...inputStyle, width: 80, marginBottom: 0 }} type="number" placeholder="sec" value={ch.timestamp} onChange={e => updateChapter(i, 'timestamp', e.target.value)} />
            <button onClick={() => removeChapter(i)} style={{ ...btnStyle, background: '#ef4444', color: '#fff' }}>x</button>
          </div>
        ))}
      </div>

      {/* Transcript + Images */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h4 style={{ color: isDarkMode ? '#e2e8f0' : '#1e293b', margin: 0 }}>Transcript</h4>
          <button onClick={addTranscript} style={{ ...btnStyle, background: '#8b5cf6', color: '#fff' }}>+ Add</button>
        </div>
        <p style={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: 12, margin: '0 0 10px 0' }}>Add multiple images per paragraph — set time range for each. No image at current time = cover shows.</p>
        {(ep.transcript || []).map((t, i) => (
          <div key={i} style={{ marginBottom: 10, background: isDarkMode ? '#0f172a' : '#f8fafc', borderRadius: 8, padding: 10, border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}` }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <span style={{ color: '#8b5cf6', fontWeight: 700, fontSize: 12, minWidth: 20 }}>#{i + 1}</span>
              <input style={{ ...inputStyle, flex: 1, marginBottom: 0 }} placeholder="Text" value={t.text} onChange={e => updateTranscript(i, 'text', e.target.value)} />
              <input style={{ ...inputStyle, width: 60, marginBottom: 0, fontSize: 13 }} type="number" placeholder="from" value={t.startTime} onChange={e => updateTranscript(i, 'startTime', e.target.value)} />
              <input style={{ ...inputStyle, width: 60, marginBottom: 0, fontSize: 13 }} type="number" placeholder="to" value={t.endTime} onChange={e => updateTranscript(i, 'endTime', e.target.value)} />
              <button onClick={() => removeTranscript(i)} style={{ ...btnStyle, background: '#ef4444', color: '#fff' }}>x</button>
            </div>
            <div style={{ paddingLeft: 28 }}>
              {(t.images || []).map((img, ii) => (
                <div key={ii} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
                  <button onClick={() => uploadImage(url => updateTranscriptImage(i, ii, 'url', url))} style={{ ...btnStyle, background: '#3b82f6', color: '#fff', whiteSpace: 'nowrap', fontSize: 11 }}>Upload</button>
                  <input style={{ ...inputStyle, flex: 1, marginBottom: 0, fontSize: 12 }} placeholder="Image/GIF URL" value={img.url || ''} onChange={e => updateTranscriptImage(i, ii, 'url', e.target.value)} />
                  <input style={{ ...inputStyle, width: 50, marginBottom: 0, fontSize: 11 }} type="number" placeholder="from" value={img.startTime} onChange={e => updateTranscriptImage(i, ii, 'startTime', e.target.value)} />
                  <input style={{ ...inputStyle, width: 50, marginBottom: 0, fontSize: 11 }} type="number" placeholder="to" value={img.endTime} onChange={e => updateTranscriptImage(i, ii, 'endTime', e.target.value)} />
                  <button onClick={() => removeTranscriptImage(i, ii)} style={{ ...btnStyle, background: '#ef4444', color: '#fff', fontSize: 11 }}>x</button>
                  {img.url && <img src={img.url} alt="" style={{ width: 40, height: 28, objectFit: 'cover', borderRadius: 4 }} />}
                </div>
              ))}
              <button onClick={() => addTranscriptImage(i)} style={{ ...btnStyle, background: 'transparent', color: '#3b82f6', border: '1px dashed #3b82f6', fontSize: 11, marginTop: 2 }}>+ Add Image</button>
            </div>
          </div>
        ))}
      </div>


      {/* Darija Phrases */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h4 style={{ color: isDarkMode ? '#e2e8f0' : '#1e293b', margin: 0 }}>Darija Phrases</h4>
          <button onClick={addPhrase} style={{ ...btnStyle, background: '#8b5cf6', color: '#fff' }}>+ Add</button>
        </div>
        {(ep.phrases || []).map((p, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
            <input style={{ ...inputStyle, flex: 1, marginBottom: 0 }} placeholder="Latin (B'shhal?)" value={p.latin} onChange={e => updatePhrase(i, 'latin', e.target.value)} />
            <input style={{ ...inputStyle, flex: 1, marginBottom: 0 }} placeholder="Arabic" value={p.arabic} onChange={e => updatePhrase(i, 'arabic', e.target.value)} />
            <input style={{ ...inputStyle, flex: 1, marginBottom: 0 }} placeholder="English" value={p.english} onChange={e => updatePhrase(i, 'english', e.target.value)} />
            <button onClick={() => removePhrase(i)} style={{ ...btnStyle, background: '#ef4444', color: '#fff' }}>x</button>
          </div>
        ))}
      </div>

      {/* Quiz Questions */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h4 style={{ color: isDarkMode ? '#e2e8f0' : '#1e293b', margin: 0 }}>Quiz Questions</h4>
          <button onClick={addQuiz} style={{ ...btnStyle, background: '#8b5cf6', color: '#fff' }}>+ Add</button>
        </div>
        {(ep.quizQuestions || []).map((q, qi) => (
          <div key={qi} style={{ background: isDarkMode ? '#1e293b' : '#fff', borderRadius: 8, padding: 12, marginBottom: 8, border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}` }}>
            <input style={inputStyle} placeholder="Question" value={q.question} onChange={e => updateQuiz(qi, 'question', e.target.value)} />
            {(q.options || []).map((opt, oi) => (
              <div key={oi} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                <input type="radio" name={`quiz-${qi}`} checked={q.correctIndex === oi} onChange={() => updateQuiz(qi, 'correctIndex', oi)} />
                <input style={{ ...inputStyle, flex: 1, marginBottom: 0 }} placeholder={`Option ${oi + 1}`} value={opt} onChange={e => updateQuizOption(qi, oi, e.target.value)} />
              </div>
            ))}
            <button onClick={() => removeQuiz(qi)} style={{ ...btnStyle, background: '#ef4444', color: '#fff', marginTop: 4 }}>Remove Question</button>
          </div>
        ))}
      </div>

      {/* Save/Cancel */}
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button onClick={() => onSave(ep)} style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: '#8b5cf6', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>Save Episode</button>
        <button onClick={onCancel} style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: isDarkMode ? '#334155' : '#e2e8f0', color: isDarkMode ? '#e2e8f0' : '#475569', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
      </div>
    </div>
  );
};


export default CulturePodAdminPanel;
